import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["document", "name", "emailAddress", "whatsappNumber", "zipCode", "city", "state", "addressLine", "addressNumber", "status"]
  static values = { url: String }

  connect() {
    this.lookupTimeout = null
    this.abortController = null
    this.lastFetchedCnpj = null
  }

  disconnect() {
    clearTimeout(this.lookupTimeout)
    this.abortController?.abort()
  }

  scheduleLookup() {
    if (!this.hasCompleteCnpj()) {
      this.resetLookup()
      return
    }

    clearTimeout(this.lookupTimeout)
    this.lookupTimeout = setTimeout(() => this.lookup(), 300)
  }

  lookupOnBlur() {
    clearTimeout(this.lookupTimeout)

    if (this.hasCompleteCnpj()) {
      this.lookup()
    }
  }

  async lookup() {
    clearTimeout(this.lookupTimeout)

    const cnpj = this.normalizedDocument()

    if (cnpj.length !== 14 || cnpj === this.lastFetchedCnpj) {
      return
    }

    this.abortController?.abort()
    this.abortController = new AbortController()
    this.showStatus("Buscando dados do CNPJ...", false)

    try {
      const response = await fetch(`${this.urlValue}?cnpj=${encodeURIComponent(cnpj)}`, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "lookup_failed")
      }

      this.applyLookupResult(data)
      this.lastFetchedCnpj = cnpj
      this.showStatus("Dados da empresa preenchidos automaticamente.", false)
    } catch (error) {
      if (error.name === "AbortError") {
        return
      }

      if (this.lastFetchedCnpj && cnpj !== this.lastFetchedCnpj) {
        this.clearAutofilledFields()
      }

      this.lastFetchedCnpj = null
      this.showStatus(error.message || "Nao foi possivel consultar o CNPJ agora.", true)
    }
  }

  applyLookupResult(data) {
    if (this.hasDocumentTarget && this.hasValue(data, "document")) {
      this.documentTarget.value = data.document
    }

    if (this.hasNameTarget && this.hasValue(data, "name")) {
      this.nameTarget.value = data.name
    }

    if (this.hasEmailAddressTarget && this.hasValue(data, "email_address")) {
      this.emailAddressTarget.value = data.email_address
    }

    if (this.hasWhatsappNumberTarget && this.hasValue(data, "whatsapp_number")) {
      this.whatsappNumberTarget.value = data.whatsapp_number
    }

    if (this.hasZipCodeTarget && this.hasValue(data, "zip_code")) {
      this.zipCodeTarget.value = data.zip_code
    }

    if (this.hasCityTarget && this.hasValue(data, "city")) {
      this.cityTarget.value = data.city
    }

    if (this.hasStateTarget && this.hasValue(data, "state")) {
      this.stateTarget.value = data.state
    }

    if (this.hasAddressLineTarget && this.hasValue(data, "address_line")) {
      this.addressLineTarget.value = data.address_line
    }

    if (this.hasAddressNumberTarget && this.hasValue(data, "address_number")) {
      this.addressNumberTarget.value = data.address_number
    }
  }

  normalizedDocument() {
    return this.documentTarget.value.replace(/\D/g, "").slice(0, 14)
  }

  hasCompleteCnpj() {
    return this.normalizedDocument().length === 14
  }

  resetLookup() {
    clearTimeout(this.lookupTimeout)
    this.abortController?.abort()

    if (this.lastFetchedCnpj) {
      this.clearAutofilledFields()
    }

    this.lastFetchedCnpj = null
    this.resetStatus()
  }

  clearAutofilledFields() {
    if (this.hasNameTarget) {
      this.nameTarget.value = ""
    }

    if (this.hasZipCodeTarget) {
      this.zipCodeTarget.value = ""
    }

    if (this.hasEmailAddressTarget) {
      this.emailAddressTarget.value = ""
    }

    if (this.hasWhatsappNumberTarget) {
      this.whatsappNumberTarget.value = ""
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = ""
    }

    if (this.hasStateTarget) {
      this.stateTarget.value = ""
    }

    if (this.hasAddressLineTarget) {
      this.addressLineTarget.value = ""
    }

    if (this.hasAddressNumberTarget) {
      this.addressNumberTarget.value = ""
    }
  }

  hasValue(data, key) {
    return Object.prototype.hasOwnProperty.call(data, key)
  }

  resetStatus() {
    if (!this.hasStatusTarget) {
      return
    }

    this.statusTarget.textContent = ""
    this.statusTarget.classList.add("hidden")
    this.statusTarget.classList.remove("text-red-600")
    this.statusTarget.classList.add("text-slate-500")
  }

  showStatus(message, isError) {
    if (!this.hasStatusTarget) {
      return
    }

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden", "text-red-600", "text-slate-500")
    this.statusTarget.classList.add(isError ? "text-red-600" : "text-slate-500")
  }
}
